/**
 * «Добавить кассу»: pick the cash register, get a one-time code, type it on
 * the new computer. The screen watches the code and says when the register is
 * connected. The code is not a sign-in — it binds one register, once, within
 * 10 minutes.
 */
import type { PairingCodeDto, PairingStatus, RegisterDto } from '@pos/api-client';
import { ApiError } from '@pos/api-client';
import { BottomSheet, Button, Field, Input, Notice, Select, toast } from '@pos/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, useAuth } from '../../app/auth';
import { useBranches, useEmployees } from '../../app/data';

const PIN_RE = /^[0-9]{4,8}$/;

function useCountdown(until: string | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  return until ? Math.max(0, Math.ceil((new Date(until).getTime() - now) / 1000)) : 0;
}

const registerLabel = (r: { number: number; name: string | null }) =>
  r.name && r.name !== `Касса ${r.number}` ? `Касса №${r.number} · ${r.name}` : `Касса №${r.number}`;

export function AddRegisterSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const me = useAuth((s) => s.me);
  const branches = useBranches().data ?? [];
  const employees = useEmployees().data ?? [];
  const registers = useQuery({
    queryKey: ['registers', useAuth.getState().businessId],
    queryFn: () => api.get<RegisterDto[]>('/registers'),
    enabled: open,
  });
  const [registerId, setRegisterId] = useState('');
  const [pin, setPin] = useState('');
  const [made, setMade] = useState<PairingCodeDto | null>(null);

  const active = (registers.data ?? []).filter((r) => r.isActive);
  const chosen = registerId || (active.find((r) => r.devices.length === 0) ?? active[0])?.id || '';
  const chosenRegister = active.find((r) => r.id === chosen);
  const ownerRow = employees.find((e) => e.userId === me?.user.id);
  const needsPin = ownerRow ? ownerRow.hasPin === false : false;
  const pinBad = pin !== '' && !PIN_RE.test(pin);
  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? '';

  const create = useMutation({
    mutationFn: () =>
      api.post<PairingCodeDto>('/devices/pairing-codes', {
        cashRegisterId: chosen,
        ...(needsPin && pin ? { ownerPin: pin } : {}),
      }),
    onSuccess: (res) => {
      setMade(res);
      if (needsPin) void qc.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (e) =>
      toast.error('Код не создан', {
        description:
          e instanceof ApiError
            ? e.isNetwork
              ? 'Нет связи с сервером'
              : (Object.values(e.fields)[0] ?? e.message)
            : undefined,
      }),
  });

  const status = useQuery({
    queryKey: ['pairing', made?.id],
    queryFn: () => api.get<PairingStatus>(`/devices/pairing-codes/${made?.id ?? ''}`),
    enabled: Boolean(made),
    refetchInterval: (q) => (q.state.data?.status === 'waiting' || !q.state.data ? 2500 : false),
    refetchOnWindowFocus: true,
  });
  const left = useCountdown(made && status.data?.status !== 'connected' ? made.expiresAt : null);
  const connected = status.data?.status === 'connected';
  const expired = !connected && made !== null && (status.data?.status === 'expired' || left === 0);

  useEffect(() => {
    if (connected) void qc.invalidateQueries({ queryKey: ['devices'] });
  }, [connected, qc]);

  const close = () => {
    // A code nobody used is withdrawn when the owner leaves the screen.
    if (made && !connected && !expired)
      void api.delete(`/devices/pairing-codes/${made.id}`).catch(() => undefined);
    setMade(null);
    setPin('');
    setRegisterId('');
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={(v) => !v && close()}
      title={made ? registerLabel(made.register) : 'Добавить кассу'}
      description={
        made
          ? made.register.branchName
          : 'Подключите компьютер с программой Gertsak к кассе вашего магазина.'
      }
      footer={
        !made ? (
          <Button
            size="xl"
            variant="primary"
            className="w-full"
            disabled={!chosen || (needsPin && !PIN_RE.test(pin))}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Получить код
          </Button>
        ) : connected ? (
          <Button size="xl" variant="primary" className="w-full" onClick={close}>
            Готово
          </Button>
        ) : expired ? (
          <Button
            size="xl"
            variant="primary"
            className="w-full"
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Новый код
          </Button>
        ) : (
          <Button size="xl" className="w-full" onClick={close}>
            Отменить
          </Button>
        )
      }
    >
      {!made ? (
        registers.isError ? (
          <Notice tone="danger">Не удалось загрузить кассы. Проверьте интернет.</Notice>
        ) : active.length === 0 && registers.isSuccess ? (
          <Notice tone="warning">
            В магазине нет касс. Добавьте кассу в филиале на кассовом компьютере: Администрирование
            → Кассы.
          </Notice>
        ) : (
          <div className="space-y-4">
            <Field label="Какая касса" hint="Код подключит компьютер именно к ней">
              {(a) => (
                <Select
                  {...a}
                  size="lg"
                  value={chosen}
                  onValueChange={setRegisterId}
                  options={active.map((r) => ({
                    value: r.id,
                    label: `${registerLabel(r)}${branches.length > 1 ? ` · ${branchName(r.branchId)}` : ''}${r.devices.length ? ' · подключена' : ''}`,
                  }))}
                />
              )}
            </Field>
            {chosenRegister?.devices[0] && (
              <Notice tone="warning">
                К этой кассе уже подключён компьютер «{chosenRegister.devices[0].name}». Новый
                займёт его место, а старый отключится.
              </Notice>
            )}
            {needsPin && (
              <Field
                label="Ваш PIN для кассы"
                hint="4–8 цифр. С ним вы войдёте на новой кассе, в том числе без интернета."
                error={pinBad ? 'PIN — от 4 до 8 цифр' : undefined}
              >
                {(a) => (
                  <Input
                    {...a}
                    size="lg"
                    type="password"
                    inputMode="numeric"
                    autoComplete="new-password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  />
                )}
              </Field>
            )}
          </div>
        )
      ) : connected ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircle2 className="size-14 text-success" aria-hidden />
          <p className="text-xl font-semibold">Касса подключена</p>
          <p className="text-base text-ink-3">
            {status.data?.status === 'connected' && status.data.device
              ? `Компьютер «${status.data.device.name}» работает как `
              : 'Компьютер работает как '}
            {registerLabel(made.register)}. Код больше не действует.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className={expired ? 'opacity-40' : undefined}>
            <p
              className="text-center font-mono text-[44px] font-semibold leading-none tracking-[0.12em] tabular-nums"
              aria-label={`Код ${made.code.split('').join(' ')}`}
            >
              {made.code}
            </p>
            <p className="mt-3 text-center text-base text-ink-3" aria-live="polite">
              {expired
                ? 'Код истёк'
                : `Действует ещё ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`}
            </p>
          </div>
          {expired ? (
            <Notice tone="warning">Код больше не работает. Получите новый — это безопасно.</Notice>
          ) : (
            <ol className="list-decimal space-y-1 pl-5 text-base text-ink-2">
              <li>Откройте программу Gertsak на компьютере кассы.</li>
              <li>На экране «Подключение кассы» введите этот код.</li>
              <li>Этот экран сам покажет, когда касса подключится.</li>
            </ol>
          )}
          <p className="text-sm text-ink-3">
            Код одноразовый. Не пересылайте его посторонним: он подключает компьютер к вашему
            магазину.
          </p>
        </div>
      )}
    </BottomSheet>
  );
}
